import { useContainerDimensions } from 'features/dashboard/widgets/firstline/FirstlineDataViewerDesktop';
import { JSX, useMemo, useRef } from 'react';

import { IColumnConfig, UserProfileWithStructure } from './types';
import './UserTable.scss';
import { UserTableRow } from './UserTableRow';

interface IUserTableProps {
  readonly users: ReadonlyArray<UserProfileWithStructure>;
  readonly columns: IColumnConfig[];
  readonly renderExpandedBlock?: (
    user: UserProfileWithStructure
  ) => JSX.Element | null;
  readonly tableRowClassName?:
    | string
    | ((user: UserProfileWithStructure) => string);
}

export function UserTable({
  users,
  columns,
  tableRowClassName,
  renderExpandedBlock
}: IUserTableProps) {
  const componentRef = useRef<HTMLTableElement>(null);
  const { width } = useContainerDimensions(componentRef);

  const headerColumns = useMemo(() => {
    const headerColumns: IColumnConfig[] = [];
    let skipNext = 0;

    columns.forEach(column => {
      if (skipNext > 0) {
        skipNext--;
        return;
      }

      headerColumns.push(column);

      // If this column has colSpan > 1, skip the next (colSpan - 1) columns
      if (column.colSpan && column.colSpan > 1) {
        skipNext = column.colSpan - 1;
      }
    });

    return headerColumns;
  }, [columns]);

  return (
    <div className="block overflow-y-auto" ref={componentRef}>
      <table className="w-full table-auto">
        <thead className="bg-body sticky top-0">
          <tr className="divide-x divide-slate-100">
            {headerColumns.map(column => (
              <th
                key={column.key}
                colSpan={column.colSpan || 1}
                className={`text-md font-medium text-gray-900 p-2 text-middle ${
                  column.className || ''
                }`}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <UserTableRow
              containerWidth={width}
              user={user}
              columns={columns}
              key={`user__${user.userId}`}
              renderExpandedBlock={renderExpandedBlock}
              className={tableRowClassName}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
